import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import Combobox from "@minohealth/common/components/combobox";
import AnokyeApiService from "@minohealth/common/services/AnokyeApiService";
import ApiService from "@minohealth/common/services/ApiService";
import useDialogOpenState from "@minohealth/common/hooks/useDialogOpenState";
import { showConfirm } from "@minohealth/common/utils/confirmers";
import { showErrorMessage, showSuccessMessage } from "@minohealth/common/utils";
import SuccessDialog from "./SuccessDialog";

import { HiMiniPaperClip } from "react-icons/hi2";
import { IoMdClose } from "react-icons/io";
import { FaAngleRight } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import userAvatar from "../../../../assets/icons/user-image.png";

import "./anokye-sharing-window.css";
import LowCreditsDialog from "@/components/ui/LowCreditsDialog";
import { ErrorCodes, parseAnokyeError, getUserFriendlyMessage } from "@/services/anokye-error-handler";

const AnokyeSharingWindow = Object.assign(
  forwardRef(
    /**
     * @param {object} props
     * @param {string} props.resourceId
     * @param {string} props.resourceType
     * @param {React.ReactNode} props.children
     * @param {(ev: any) => void} [props.onClose]
     * @param {React.ForwardedRef<AnokyeSharingWindowComponent>} ref
     */
    function AnokyeSharingWindow({ resourceId, resourceType, children, onClose }, ref) {
      const { user } = useSelector((state) => state.app.datastate);
      const { rankedReviewers, selectedReviewers, setSelectedReviewers } = useAnokyeReviewerState(
        resourceId,
        {
          onError: handleReviewerStateError,
        }
      );
      const {
        members,
        setMembers,
        isMembersUpdated,
        alreadyHasMembers,
        filterValue,
        setFilterValue,
        filterResults,
        setFilterResults,
      } = useAnokyeMembersState(resourceId, {
        onError: handleMembersStateError,
      });

      const dialogRef = useRef(null);
      const { openWindow, closeWindow } = useAnokyeWindowActions(dialogRef);

      useImperativeHandle(ref, () => ({
        openWindow,
        closeWindow,
      }));

      useEffect(() => {
        if (user._id == null) return;
        return () => closeWindow();
      }, [user._id]);

      function handleMembersStateError(error) {
        showErrorMessage(error.message, "Sharing Participants Error");
        closeWindow();
      }

      function handleReviewerStateError(error) {
        showErrorMessage(error.message, "Ranked Reviewers Error");
        closeWindow();
      }

      return (
        <anokyeSharingWindowContext.Provider
          value={{
            members,
            isMembersUpdated,
            alreadyHasMembers,
            rankedReviewers,
            selectedReviewers,
            setSelectedReviewers,
            resourceId,
            resourceType,
            setMembers,
            closeWindow,
            filterValue,
            setFilterValue,
            filterResults,
            setFilterResults,
          }}>
          <dialog className="sharing-window" onClose={onClose} ref={dialogRef}>
            <article className="sharing-window__body">{children}</article>
          </dialog>
        </anokyeSharingWindowContext.Provider>
      );
    }
  ),
  {
    Header: SharingWindowHeader,
    Content: SharingWindowContent,
    ResourceDetails: SharingWindowResourceDetails,
    ResourceInfo: SharingWindowResourceInfo,
    ResourceStats: SharingWindowResourceStats,
    LinkSharing: SharingWindowLinkSharing,
    SearchMembers: SharingWindowSearchMembers,
    MembersInfo: SharingWindowMembersInfo,
    ReviewersInfo: SharingWindowReviewersInfo,
    SelectedReviewers: SharingWindowSelectedReviewers,
    Footer: SharingWindowFooter,
    ReviewFooter: ReviewerWindowFooter,
  }
);

export default AnokyeSharingWindow;

export const ResourceType = /** @type {const{ */ ({
  PROJECT: "projects",
  CHAT: "chats",
  REPORT: "reports",
});
/** @typedef {typeof ResourceType[keyof typeof ResourceType]} ResourceTypeValue */

/**
 * @typedef {Object} AnokyeSharingWindowComponent
 * @property {() => void} openWindow
 * @property {() => void} closeWindow
 */

const anokyeSharingWindowContext = createContext({
  /** @type {Member[] | null} */
  members: null,
  alreadyHasMembers: false,
  isMembersUpdated: false,
  resourceId: "",
  /** @type {ResourceTypeValue} */
  resourceType: "",
  /** @type {React.Dispatch<React.SetStateAction<Member[]>>} */
  setMembers: () => {},
  closeWindow: () => {},
  selectedReviewers: [],
  setSelectedReviewers: () => {},
  rankedReviewers: null,
});

/**
 * @param {string} resourceId
 * @param {object} options
 * @param {(error: any) => void} [options.onError]
 */
function useAnokyeMembersState(resourceId, { onError }) {
  const { user } = useSelector((state) => state.app.datastate);
  const [initialMembers, setInitialMembers] = useState(null);
  const [members, setMembers] = useState(null);
  const [filterResults, setFilterResults] = useState([]);
  const [filterValue, setFilterValue] = useState("");

  const isMembersUpdated = useMemo(() => {
    if (initialMembers == null || members == null) return false;
    if (initialMembers.length !== members.length) return true;
    if (initialMembers.length === 0 && members.length === 0) return false;
    return members.some(
      (member) => initialMembers.find((initialMember) => initialMember._id === member._id) == null
    );
  });
  const alreadyHasMembers = initialMembers != null && initialMembers.length > 0;
  useEffect(() => {
    if (resourceId == null) {
      setMembers(null);
      return;
    }

    async function getResourceParticipants() {
      try {
        const { data } = await AnokyeApiService(user?.userToken).GetParticipants({
          resourceId,
          action: "participants",
        });
        setInitialMembers(data?.payload);
        setMembers(data?.payload);
      } catch (error) {
        const parsedError = parseAnokyeError(error);

        Sentry.captureException(error, {
          extra: {
            context: 'AnokyeSharingWindow - getResourceParticipants',
            resourceId: resourceId || 'undefined',
            action: 'participants',
            userToken: user?.userToken ? 'present' : 'missing',
            userId: user?._id || 'undefined',
            errorCode: parsedError.code,
            errorMessage: parsedError.message
          }
        });

        // Create a user-friendly error to pass to error handler
        const userError = new Error(getUserFriendlyMessage(parsedError.code, parsedError.message));
        userError.code = parsedError.code;
        onError?.(userError);
      }
    }
    getResourceParticipants();
  }, [resourceId]);

  return {
    members,
    setMembers,
    isMembersUpdated,
    alreadyHasMembers,
    filterValue,
    setFilterValue,
    filterResults,
    setFilterResults,
  };
}

/**
 * @param {string} resourceId
 * @param {object} options
 * @param {(error: any) => void} [options.onError]
 */
function useAnokyeReviewerState(resourceId, { onError }) {
  const { user } = useSelector((state) => state.app.datastate);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [rankedReviewers, setRankedReviewers] = useState(null);

  useEffect(() => {
    if (resourceId == null) {
      setRankedReviewers(null);
      return;
    }

    async function getResourceReviewers() {
      try {
        const { data } = await AnokyeApiService(user?.userToken).GetRankedReviewers({
          specialist: "getReviewers",
          reportModality: "Chest X-Ray",
          userId: user?._id
        });
        setRankedReviewers(data);
        setSelectedReviewers(data.length ? [data[0]] : []);
      } catch (error) {
        const parsedError = parseAnokyeError(error);

        Sentry.captureException(error, {
          extra: {
            context: 'AnokyeSharingWindow - getResourceReviewers',
            resourceId: resourceId || 'undefined',
            specialist: 'getReviewers',
            reportModality: 'Chest X-Ray',
            userToken: user?.userToken ? 'present' : 'missing',
            userId: user?._id || 'undefined',
            errorCode: parsedError.code,
            errorMessage: parsedError.message
          }
        });

        const userError = new Error(getUserFriendlyMessage(parsedError.code, parsedError.message));
        userError.code = parsedError.code;
        onError?.(userError);
      }
    }
    getResourceReviewers();
  }, [resourceId]);

  return { rankedReviewers, selectedReviewers, setSelectedReviewers };
}

/**
 * @param {React.RefObject<HTMLDialogElement>} dialogRef
 */
function useAnokyeWindowActions(dialogRef) {
  const [_, setIsDialogOpen] = useDialogOpenState(dialogRef, { closeOnEscape: false });

  function openWindow() {
    setIsDialogOpen(true);
  }

  function closeWindow() {
    setIsDialogOpen(false);
  }

  return { openWindow, closeWindow };
}

/**
 * @param {object} props
 * @param {string} title
 * @param {string} [subtitle]
 */
function SharingWindowHeader({ title, subtitle }) {
  return (
    <header className="sharing-window__header">
      <h2 className="sharing-window__title">{title}</h2>
      {subtitle && <p className="sharing-window__subtitle">{subtitle}</p>}
    </header>
  );
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function SharingWindowContent({ children }) {
  return <div className="sharing-window__content">{children}</div>;
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function SharingWindowResourceDetails({ children }) {
  return <div className="sharing-window__resource-details">{children}</div>;
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function SharingWindowResourceInfo({ children }) {
  return <div className="sharing-window__resource-info">{children}</div>;
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function SharingWindowResourceStats({ children }) {
  return (
    <div className="sharing-window__resource-stats">
      <span>•</span>
      <span>{children}</span>
    </div>
  );
}

const MemberRole = /** @type {const{ */ ({
  EDITOR: "editor",
  VIEWER: "viewer",
});
/** @typedef {typeof MemberRole[keyof typeof MemberRole]} MemberRoleValue */

const MembersFilter = /** @type {const{ */ ({
  ALL: "all",
  EDITORS: "editors",
  VIEWERS: "viewers",
});
/** @typedef {typeof MembersFilter[keyof typeof MembersFilter]} MembersFilterValue */

function SharingWindowSearchMembers() {
  const { members, setMembers, resourceId, filterValue, setFilterValue, filterResults, setFilterResults } =
    useContext(anokyeSharingWindowContext);
  const { user } = useSelector((state) => state.app.datastate);

  useEffect(() => {
    if (resourceId == null) return;

    async function searchByFilter() {
      try {
        const { status, data } = await ApiService(user?.userToken).SearchUser({ query: filterValue });
        if (status.toString().match(/200/)) {
          setFilterResults(data?.payload?.users);
        }
      } catch (error) {
        const parsedError = parseAnokyeError(error);

        Sentry.captureException(error, {
          extra: {
            context: 'AnokyeSharingWindow - searchByFilter',
            resourceId: resourceId || 'undefined',
            filterValue: filterValue || 'empty',
            userToken: user?.userToken ? 'present' : 'missing',
            userId: user?._id || 'undefined',
            errorCode: parsedError.code,
            errorMessage: parsedError.message
          }
        });

        // Only show error for critical errors (not 404/not found)
        if (![ErrorCodes.RESOURCE_NOT_FOUND, ErrorCodes.SESSION_NOT_FOUND].includes(parsedError.code)) {
          console.warn('Search filter error:', parsedError.message);
        }
      }
    }
    searchByFilter();
  }, [filterValue]);

  const allowedFilterResults = filterResults?.filter(
    (result) => members?.find((member) => member._id === result._id) == null || user?._id === result._id
  );

  return (
    <div className="sharing-window__search-members">
      <Combobox
        className="sharing-window__search-members-field"
        onSelectOption={(value) => {
          setMembers((prev) => {
            const newMember = filterResults.find((user) => user.email === value);
            if (newMember == null) return prev;
            return [...prev, { ...newMember, role: MemberRole.VIEWER }];
          });
          setFilterValue("");
        }}>
        <Combobox.Input
          type="search"
          placeholder="Search members"
          className="sharing-window__search-members-input"
          onChange={(ev) => setFilterValue(ev.target.value)}
        />

        <Combobox.Listbox className="sharing-window__search-members-listbox">
          {allowedFilterResults.map((user) => (
            <Combobox.Option
              value={user?.email}
              className="sharing-window__search-members-option"
              key={user?.email}>
              <img
                className="sharing-window__search-members-option-avatar"
                src={userAvatar}
                alt="User avatar"
              />
              <div className="sharing-window__search-members-option-info">
                <p>
                  {user?.firstName} {user?.lastName}
                </p>
                <p>{user?.email}</p>
              </div>
              <div className="sharing-window__search-members-option-invite">
                <p>Invite</p>
                <FaAngleRight />
              </div>
            </Combobox.Option>
          ))}
        </Combobox.Listbox>
      </Combobox>
    </div>
  );
}

/**
 * @typedef {object} Member
 * @property {string} name
 * @property {string} email
 * @property {MemberRoleValue} role
 */

function SharingWindowMembersInfo() {
  const { user } = useSelector((state) => state.app.datastate);
  const { members, setMembers } = useContext(anokyeSharingWindowContext);
  const [selectedMembersFilter, setSelectedMembersFilter] = useState(MembersFilter.ALL);

  const filteredMembers = members
    ?.filter((member) => member._id !== user._id)
    ?.filter(
      (member) =>
        selectedMembersFilter === MembersFilter.ALL ||
        (selectedMembersFilter === MembersFilter.EDITORS && member.role === MemberRole.EDITOR) ||
        (selectedMembersFilter === MembersFilter.VIEWERS && member.role === MemberRole.VIEWER)
    );

  const reverseFilteredMembers = filteredMembers != null ? [...filteredMembers].reverse() : null;

  const noUsersMessage = (() => {
    if (selectedMembersFilter === MembersFilter.ALL) return "Not shared with any users";
    if (selectedMembersFilter === MembersFilter.EDITORS) return "Not shared with any users with editor role";
    if (selectedMembersFilter === MembersFilter.VIEWERS) return "Not shared with any users with viewer role";
  })();

  return (
    <div className="sharing-window__members-info">
      <div className="sharing-window__members-info-header">
        <p className="sharing-window__members-info-title">Shared with members</p>
        {/*
        <fieldset className="sharing-window__members-filter-fieldset">
          <legend className="sr-only">Members filter</legend>
          <MembersFilterField
            field={{ name: "All", value: MembersFilter.ALL }}
            selectedMembersFilter={selectedMembersFilter}
            setSelectedMembersFilter={setSelectedMembersFilter}
          />

            field={{ name: "Editors", value: MembersFilter.EDITORS }}
            selectedMembersFilter={selectedMembersFilter}
            setSelectedMembersFilter={setSelectedMembersFilter}
          />
          <MembersFilterField
            field={{ name: "Viewers", value: MembersFilter.VIEWERS }}
            selectedMembersFilter={selectedMembersFilter}
            setSelectedMembersFilter={setSelectedMembersFilter}
          />
        </fieldset>
          */}
      </div>

      {reverseFilteredMembers == null && (
        <div className="flow" style={{ "--flow-gap": "0.25rem" }}>
          <MembersListLoadingIndicator />
        </div>
      )}

      {reverseFilteredMembers?.length === 0 && (
        <div className="sharing-window__no-members-info">
          <p>{noUsersMessage}</p>
        </div>
      )}

      {reverseFilteredMembers?.length > 0 && (
        <ul className="sharing-window__members-list sharing-window__reviewers-list">
          {reverseFilteredMembers.map((filteredMember, memberIndex) => (
            <li className="sharing-window__members-list-item" key={filteredMember.email}>
              <div className="sharing-window__members-list-item-info">
                <img
                  src={userAvatar}
                  alt="User avatar"
                  className="sharing-window__members-list-item-avatar"
                />
                {filteredMember.firstName && filteredMember.lastName ? (
                  <p className="sharing-window__members-list-item-name">
                    {filteredMember?.firstName} {filteredMember?.lastName}
                  </p>
                ) : (
                  <p className="sharing-window__members-list-item-name">{filteredMember?.email}</p>
                )}
                <button
                  type="button"
                  className="sharing-window__members-list-item-remove-btn"
                  title="Remove member"
                  onClick={(ev) => {
                    mixpanel.track("Remove Sharing Member", {
                      memberEmail: filteredMember.email,
                      resourceType: "report"
                    })
                    setMembers((prev) => prev.filter((member) => member.email != filteredMember.email));
                  }}>
                  <IoMdClose />
                  <span className="sr-only">Remove member</span>
                </button>
              </div>

              <div className="sharing-window__members-list-item-role">
                {/*
                <select
                  defaultValue={filteredMember.role}
                  onChange={(ev) => {
                    setMembers((prev) =>
                      prev.map((member, index) => {
                        if (member.email != filteredMember.email) return member;
                        return { ...member, role: ev.target.value };
                      })
                    );
                  }}>
                  <option value={MemberRole.EDITOR}>Can Edit</option>
                  <option value={MemberRole.VIEWER}>Can View</option>
                </select>
                  */}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * @typedef {object} Member
 * @property {string} name
 * @property {string} email
 * @property {MemberRoleValue} role
 */

function SharingWindowReviewersInfo() {
  const { rankedReviewers, filterResults, selectedReviewers, setSelectedReviewers, members } =
    useContext(anokyeSharingWindowContext);
  const [activeTab, setActiveTab] = useState("minohealth"); // 'minohealth' or 'other'

  useEffect(() => {
    if (!members) return;
    setSelectedReviewers((prev) => {
      const existingIds = new Set(prev.map((reviewer) => reviewer?._id));
      const newMembers = members.filter((member) => !existingIds.has(member._id));
      return [...prev, ...newMembers];
    });
  }, [members]);

  function handleSelectReviewer({ reviewer }) {
    if (!selectedReviewers.some((r) => r?._id === reviewer?._id)) {
      setSelectedReviewers([...selectedReviewers, reviewer]);
    }
  }

  const minoHealthReviewers = useMemo(() => {
    return rankedReviewers?.filter((reviewer) => reviewer.reviewer !== null) || [];
  }, [rankedReviewers]);

  const otherReviewers = useMemo(() => {
    return filterResults?.filter((result) => !result.reviewer) || [];
  }, [filterResults]);

  const currentReviewers = activeTab === "minohealth" ? minoHealthReviewers : otherReviewers;

  return (
    <div className="sharing-window__members-info">
      <div className="sharing-window__members-info-header">
        <div className="sharing-window__tabs">
          <button
            className={`sharing-window__tab ${
              activeTab === "minohealth" ? "sharing-window__tab--active" : ""
            }`}
            onClick={() => setActiveTab("minohealth")}>
            MinoHealth Reviewers
          </button>
          <button
            className={`sharing-window__tab ${activeTab === "other" ? "sharing-window__tab--active" : ""}`}
            onClick={() => setActiveTab("other")}>
            Other Reviewers
          </button>
        </div>
      </div>

      {rankedReviewers == null && (
        <div className="flow" style={{ "--flow-gap": "0.25rem" }}>
          <MembersListLoadingIndicator />
        </div>
      )}

      {currentReviewers?.length > 0 && (
        <ul className="sharing-window__members-list">
          {currentReviewers.map((reviewer) => (
            <li
              className="sharing-window__members-list-item"
              key={reviewer._id}
              onClick={(ev) => handleSelectReviewer({ reviewer })}>
              <div
                className="sharing-window__members-list-item-info"
                data-selected-state={selectedReviewers.some((r) => r?._id === reviewer?._id)}>
                <img
                  src={userAvatar}
                  alt="User avatar"
                  className="sharing-window__members-list-item-avatar"
                />
                {reviewer?.firstName && reviewer?.lastName ? (
                  <p className="sharing-window__members-list-item-name">
                    {reviewer?.firstName} {reviewer?.lastName}{" "}
                    <span style={{ color: "yellow" }}>
                      {"$"}
                      {reviewer?.reviewer?.rate}
                    </span>
                  </p>
                ) : (
                  <p className="sharing-window__members-list-item-name">
                    {reviewer?.email}{" "}
                    <span style={{ color: "yellow" }}>
                      {"$"}
                      {reviewer?.reviewer?.rate}
                    </span>
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {currentReviewers?.length === 0 && (
        <div className="sharing-window__empty-state">
          {activeTab === "minohealth" ? (
            <p>No minoHealth reviewers available</p>
          ) : (
            <p>Search to find other reviewers</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @typedef {object} Member
 * @property {string} name
 * @property {string} email
 * @property {MemberRoleValue} role
 */

function SharingWindowSelectedReviewers() {
  const { selectedReviewers, setSelectedReviewers } = useContext(anokyeSharingWindowContext);

  function handleUnSelectReviewer(reviewerToRemove) {
    mixpanel.track("Remove Review Requestee", {
      reviewerEmail: reviewerToRemove.email,
      reviewerName: reviewerToRemove.firstName + " " + reviewerToRemove.lastName
    })
    setSelectedReviewers(selectedReviewers.filter((reviewer) => reviewer._id !== reviewerToRemove._id));
  }

  return (
    <div className="sharing-window__members-info">
      <div className="sharing-window__members-info-header">
        <p className="sharing-window__members-info-title">{`Selected Reviewers (${selectedReviewers.length})`}</p>
      </div>

      {selectedReviewers?.length > 0 && (
        <ul className="sharing-window__members-list sharing-window__reviewers-list">
          {selectedReviewers.map((reviewer) => (
            <li
              className="sharing-window__members-list-item"
              key={reviewer?._id}
              onClick={(ev) => handleUnSelectReviewer(reviewer)}>
              <div className="sharing-window__members-list-item-info">
                <img
                  src={userAvatar}
                  alt="User avatar"
                  className="sharing-window__members-list-item-avatar"
                />
                {reviewer?.firstName && reviewer?.lastName ? (
                  <p className="sharing-window__members-list-item-name">
                    {reviewer?.firstName} {reviewer?.lastName}{" "}
                    <span style={{ color: "yellow" }}>
                      {"$"}
                      {reviewer?.reviewer?.rate}
                    </span>
                  </p>
                ) : (
                  <p className="sharing-window__members-list-item-name">
                    {reviewer?.email}{" "}
                    <span style={{ color: "yellow" }}>
                      {"$"}
                      {reviewer?.reviewer?.rate}
                    </span>
                  </p>
                )}
                <button
                  type="button"
                  className="sharing-window__members-list-item-remove-btn"
                  title="Remove reviewer"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleUnSelectReviewer(reviewer);
                  }}>
                  <IoMdClose />
                  <span className="sr-only">Remove reviewer</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MembersListLoadingIndicator() {
  return (
    <div
      className="sharing-window__members-loading-indicator"
      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", opacity: 0.5 }}>
      <div className="skeleton" style={{ width: "45%", height: "1.5rem", borderRadius: "0.75rem" }}></div>

      <div className="skeleton" style={{ height: "1.75rem", width: "15%", borderRadius: "0.5rem" }}></div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {MembersFilterValue} props.selectedMembersFilter
 * @param {(filter: MembersFilterValue) => void} props.setSelectedMembersFilter
 * @param {{name: string, value: MembersFilterValue}} props.field
 */
function MembersFilterField({ field, selectedMembersFilter, setSelectedMembersFilter }) {
  const prefixId = useId();
  const fieldId = `${prefixId}-sharing-window-members-filter-${field.value}`;

  return (
    <div className="sharing-window__members-filter-field catch-focus">
      <label htmlFor={fieldId}>{field.name}</label>
      <input
        type="radio"
        id={fieldId}
        className="sr-only"
        name="sharing-window-members-filter"
        value={field.value}
        defaultChecked={selectedMembersFilter === field.value}
        onChange={(ev) => setSelectedMembersFilter(ev.target.value)}
      />
    </div>
  );
}

function SharingWindowLinkSharing() {
  return null;
  return (
    <div className="sharing-window__link-sharing">
      <div className="sharing-window__link-sharing-info">
        <p>Invite members via a sharable link</p>
        <p>Anyone with the link can view</p>
      </div>
      <button type="button" className="sharing-window__link-sharing-copy-btn">
        <HiMiniPaperClip />
        <span>Copy link</span>
      </button>
    </div>
  );
}

function SharingWindowFooter() {
  const { isMembersUpdated, alreadyHasMembers, members, resourceId, resourceType, closeWindow } =
    useContext(anokyeSharingWindowContext);
  const { user } = useSelector((state) => state.app.datastate);
  const [loading, setLoading] = useState(false);
  const successDialogRef = useRef(null);
  const [showLowCreditsDialog, setShowLowCreditsDialog] = useState(false);
  const [lowCreditsMessage, setLowCreditsMessage] = useState("");

  // Function to get resource-specific success title
  function getSuccessTitle() {
    switch (resourceType) {
      case ResourceType.PROJECT:
        return "Project Shared Successfully";
      case ResourceType.CHAT:
        return "Chat Session Shared Successfully";
      case ResourceType.REPORT:
        return "Report Shared Successfully";
      default:
        return "Resource Shared Successfully";
    }
  }

  // Function to get resource-specific success message
  function getSuccessMessage() {
    switch (resourceType) {
      case ResourceType.PROJECT:
        return "Your project has been shared with the selected participants.";
      case ResourceType.CHAT:
        return "Your chat session has been shared with the selected participants.";
      case ResourceType.REPORT:
        return "Your report has been shared with the selected participants.";
      default:
        return "Your resource has been shared with the selected participants.";
    }
  }

  async function handleDone() {
    setLoading(true);
    try {
      mixpanel.track("Submit Share Report", {
        resourceType: resourceType,
        membersCount: members?.length || 0,
        resourceId: resourceId
      })
      await shareResource();
      successDialogRef.current?.openDialog();
      closeWindow();
    } catch (err) {
      const parsedError = parseAnokyeError(err);

      // Handle insufficient funds/credits specifically
      if (parsedError.code === ErrorCodes.INSUFFICIENT_FUNDS) {
        setLowCreditsMessage(
          parsedError.message ||
          parsedError.details?.message ||
          "No available shares or credits to share chat session."
        );
        setShowLowCreditsDialog(true);
        closeWindow(); // Ensure sharing dialog is closed so LowCreditsDialog is visible
        setLoading(false);
        return;
      }

      Sentry.captureException(err, {
        extra: {
          context: 'AnokyeSharingWindow - handleDone shareResource',
          resourceId: resourceId || 'undefined',
          resourceType: resourceType || 'undefined',
          membersCount: members?.length || 0,
          alreadyHasMembers,
          isMembersUpdated,
          userToken: user?.userToken ? 'present' : 'missing',
          userId: user?._id || 'undefined',
          errorCode: parsedError.code,
          errorMessage: parsedError.message
        }
      });

      // Use error-code-specific messages
      const baseMessage = alreadyHasMembers
        ? "Failed to update participants"
        : "Failed to share resource";
      const message = getUserFriendlyMessage(parsedError.code, parsedError.message || baseMessage);

      showErrorMessage(message, "Sharing Participants");
    } finally {
      setLoading(false);
    }
  }

  async function shareResource() {
    const { status, data } = await AnokyeApiService(user?.userToken).PostShareResource({
      resourceType,
      ...(resourceType === ResourceType.PROJECT ? { projectId: resourceId } : null),
      ...(resourceType === ResourceType.CHAT ? { chatSessionId: resourceId } : null),
      ...(resourceType === ResourceType.REPORT ? { reportId: resourceId } : null),
      srcId: user._id,
      destIds: members.map((member) => member._id),
    });

    if (!status.toString().match(/200/)) throw new Error(data?.message);
  }

  async function handleCancel() {
    if (!isMembersUpdated) {
      closeWindow();
      return;
    }

    try {
      await showConfirm("Are you sure you want to discard changes?", "Sharing Participants");
      closeWindow();
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'AnokyeSharingWindow - handleCancel showConfirm',
          resourceId: resourceId || 'undefined',
          resourceType: resourceType || 'undefined',
          isMembersUpdated,
          userId: user?._id || 'undefined'
        }
      });
      // Silent error
    }
  }

  return (
    <>
      <footer className="sharing-window__footer">
        <div className="sharing-window__footer-btn-group">
          <SharingWindowFooterButton loading={loading} onClick={handleDone}>
            Done
          </SharingWindowFooterButton>
          <SharingWindowFooterButton disabled={loading} variant="secondary" onClick={handleCancel}>
            Cancel
          </SharingWindowFooterButton>
        </div>
      </footer>
      
      <SuccessDialog
        ref={successDialogRef}
        title={getSuccessTitle()}
        message={getSuccessMessage()}
        buttonText="Continue"
        onClose={() => {
          // Optional: Add any additional logic when dialog closes
        }}
      />
      <LowCreditsDialog
        open={showLowCreditsDialog}
        onOpenChange={setShowLowCreditsDialog}
        title="Low Credits"
        description={lowCreditsMessage || "No available shares or credits to share chat session."}
        buttonText="Top up"
        onButtonClick={() => {
          setShowLowCreditsDialog(false);
          // handle routing to topup page here
        }}
      />
    </>
  );
}

function ReviewerWindowFooter() {
  const { selectedReviewers, resourceId, closeWindow } = useContext(anokyeSharingWindowContext);
  const { user } = useSelector((state) => state.app.datastate);
  const [loading, setLoading] = useState(false);
  const successDialogRef = useRef(null);
  const [showLowCreditsDialog, setShowLowCreditsDialog] = useState(false);
  const [lowCreditsMessage, setLowCreditsMessage] = useState("");

  async function handleReview() {
    setLoading(true);
    try {
      mixpanel.track("Submit Review Request Report", {
        reviewersCount: selectedReviewers?.length || 0,
        resourceId: resourceId,
        reviewerEmails: selectedReviewers?.map(r => r.email).join(",") || ""
      })
      await sendReviewRequest();
      closeWindow();
      // Show success dialog instead of snackbar
      successDialogRef.current?.openDialog();
    } catch (err) {
      const parsedError = parseAnokyeError(err);

      // Handle insufficient funds specifically for review requests
      if (parsedError.code === ErrorCodes.INSUFFICIENT_FUNDS) {
        setLowCreditsMessage(
          parsedError.message ||
          parsedError.details?.message ||
          "You do not have enough credits to request a review."
        );
        setShowLowCreditsDialog(true);
        closeWindow();
        setLoading(false);
        return;
      }

      Sentry.captureException(err, {
        extra: {
          context: 'AnokyeSharingWindow - handleReview sendReviewRequest',
          resourceId: resourceId || 'undefined',
          selectedReviewersCount: selectedReviewers?.length || 0,
          userToken: user?.userToken ? 'present' : 'missing',
          userId: user?._id || 'undefined',
          errorCode: parsedError.code,
          errorMessage: parsedError.message
        }
      });

      const message = getUserFriendlyMessage(parsedError.code, "Failed to submit for review");
      showErrorMessage(message, "Review Request");
    } finally {
      setLoading(false);
    }
  }

  async function sendReviewRequest() {
    const { status, data } = await AnokyeApiService(user?.userToken).PostReview({
      reviewType: "requestReview",
      reviewerIds: selectedReviewers.map((reviewer) => reviewer._id),
      reportId: resourceId,
      userId: user?._id,
    });

       if (!status.toString().match(/^2\d{2}$/)) throw new Error(data?.message);
  }

  async function handleCancel() {
    if (!selectedReviewers.length) {
      closeWindow();
      return;
    }

    try {
      await showConfirm("Are you sure you want to discard changes?", "Review Request");
      closeWindow();
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'AnokyeSharingWindow - ReviewerWindowFooter handleCancel showConfirm',
          resourceId: resourceId || 'undefined',
          selectedReviewersCount: selectedReviewers?.length || 0,
          userId: user?._id || 'undefined'
        }
      });
      // Silent error
    }
  }

  return (
    <>
      <footer className="sharing-window__footer">
        <div className="sharing-window__footer-btn-group">
          <SharingWindowFooterButton disabled={loading} onClick={handleReview}>
            {loading && <AiOutlineLoading3Quarters className="spin" style={{ "--duration": "5s" }} />}
            Submit For Review
          </SharingWindowFooterButton>
          <SharingWindowFooterButton disabled={loading} variant="secondary" onClick={handleCancel}>
            Cancel
          </SharingWindowFooterButton>
        </div>
      </footer>
      
      <SuccessDialog
        ref={successDialogRef}
        title="Review Request Sent"
        message="Your review request has been sent successfully. The requested reviewer has been notified."
        buttonText="Continue"
        onClose={() => {
          // Optional: Add any additional logic when dialog closes
        }}
      />
      <LowCreditsDialog
        open={showLowCreditsDialog}
        onOpenChange={setShowLowCreditsDialog}
        title="Low Credits"
        description={lowCreditsMessage || "You do not have enough credits to request a review."}
        buttonText="Top up"
        onButtonClick={() => {
          setShowLowCreditsDialog(false);
          // handle routing to topup page here
        }}
      />
    </>
  );
}

/**
 * @typedef {object} SharingWindowFooterButtonExtraProps
 * @property {"primary" | "secondary"} [variant]
 * @property {boolean} [loading]
 */

/**
 * @param {React.ComponentProps<"button"> & SharingWindowFooterButtonExtraProps} props
 */
function SharingWindowFooterButton({
  variant = "primary",
  loading,
  disabled,
  className,
  children,
  ...restOfrops
}) {
  return (
    <button
      type="button"
      className="sharing-window__footer-btn"
      data-btn-variant={variant}
      disabled={loading || disabled}
      {...restOfrops}>
      {loading && <AiOutlineLoading3Quarters className="spin" style={{ "--duration": "1s" }} />}
      {children}
    </button>
  );
}
